import React, { FC, ComponentProps as CP, ReactNode, ComponentType, useMemo } from 'react'
import { Grid, Paper, makeStyles, Box, Snackbar } from '@material-ui/core'
import sum from 'lodash-es/sum'

import config from 'config'
import Button from 'components/Button'
import pickPlayers from 'lib/teamAssistant'
import T from 'components/FormattedMessage'
import SelectedTeam from './components/SelectedTeam'
import PremiumSwitch from 'components/PremiumSwitch'
import SelectedPlayer from './components/SelectedPlayer'
import { IPlayer, IMatch, PlayerPosition } from 'protos'
import BudgetIndicator from 'components/BudgetIndicator'
import SortablePlayerList from 'components/SortablePlayerList'
import PlayerComparisonTable from 'components/PlayerComparisonTable'
import teamAssistantIcon from 'assets/images/teameditor_icon_team_assistant.svg'
import ActionContext from 'components/ActionContext'

const { TEAM_SIZE, DEFAULT_BUDGET } = config

type Props = {
  isLoading: boolean
  error?: any
  singleSelect?: boolean
  narrowOnDesktop?: boolean
  match?: IMatch
  selectedPlayers: IPlayer[]
  listedPlayers?: IPlayer[]
  availablePlayers?: IPlayer[]
  playersToCompare: IPlayer[]
  onPlayerClick: (player: IPlayer) => void
  onPlayerJerseyClick: (player: IPlayer) => void
  PlayerList: ComponentType<CP<typeof SortablePlayerList>>
  header: ReactNode
  teamAssistant?: {
    error?: any
    dismissError: () => void
    execute: (availablePlayers: IPlayer[], selectedPlayers: IPlayer[]) => void
  }
  onSubmit: (players: IPlayer[]) => void
  submitText: ReactNode
  submitting: boolean
}

const useStyles = makeStyles(theme => ({
  root: {
    paddingBottom: 48 // = button height, hardcoded in material-ui
  },
  teamStatus: {
    padding: theme.spacing(2, 2, 0, 2),
    backgroundColor: 'transparent',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
  },
  teamSticky: {
    zIndex: 1,
    height: '100%',
    position: 'sticky',
    top: 60,
    backgroundColor: theme.palette.grey[50],
  },
  teamContainer: {
    position: 'relative'
  },
  actions: {
    zIndex: theme.zIndex.appBar,
    display: 'flex',
    overflow: 'hidden',
    marginTop: theme.spacing(),
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
      position: 'fixed',
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: 0
    }
  },
  actionContainer: {
    flex: 2,
  },
  teamAssistantButton: {
    height: 48,
    borderRadius: 0,
  },
  submitButton: {
    flex: 8,
    height: 48,
    borderRadius: 0,
  },
  buttonIcon: {
    marginRight: theme.spacing(),
  },
}))

const TeamEditor: FC<Props> = ({
  isLoading,
  error,
  singleSelect,
  narrowOnDesktop,
  match,
  availablePlayers,
  listedPlayers,
  selectedPlayers,
  playersToCompare,
  onPlayerClick,
  onPlayerJerseyClick,
  PlayerList,
  header,
  teamAssistant,
  onSubmit,
  submitText,
  submitting,
}) => {
  const classes = useStyles()

  const isTeamAssistantEnabled = useMemo(() => {
    if (!teamAssistant) {
      return false
    }

    try {
      pickPlayers(availablePlayers, [])
      return true
    } catch {
      return false
    }
  }, [teamAssistant, availablePlayers])

  const hasEnoughPlayers = selectedPlayers.length === TEAM_SIZE

  const gkCount = selectedPlayers.filter(p => p.position === PlayerPosition.PP_GOALKEEPER).length
  const hasTooManyGks = gkCount > 1

  const totalBudget = DEFAULT_BUDGET
  const totalTeamCost = sum(selectedPlayers.map(player => player.cost!))
  const isOverBudget = totalTeamCost > totalBudget
  const isValidTeam = hasEnoughPlayers && !hasTooManyGks && !isOverBudget
  const isEmptyTeam = selectedPlayers.length < 1

  return (
    <Grid
      container
      direction="row-reverse"
      className={classes.root}
    >
      <Grid
        item
        xs={12}
      >
        {header}
      </Grid>
      <Grid
        item
        xs={12}
        md={narrowOnDesktop ? 6 : undefined}
        className={classes.teamSticky}
      >
        <Paper
          elevation={0}
          className={classes.teamStatus}
        >
          <BudgetIndicator
            totalBudget={totalBudget}
            remainingBudget={totalBudget - totalTeamCost}
            hasTooManyGoalkeepers={hasTooManyGks}
          />

          <div className={classes.teamContainer}>
            {singleSelect ? (
              <SelectedPlayer
                playerToSub={playersToCompare[0]}
                selectedPlayer={playersToCompare[1]}
                onPlayerClick={() => onPlayerClick(playersToCompare[1])}
              />
            ) : (
              <SelectedTeam
                selectedPlayers={selectedPlayers}
                onPlayerClick={onPlayerClick}
              />
            )}

            <Box mx={-2}>
              <PlayerComparisonTable
                totalPlayers={singleSelect ? 2 : TEAM_SIZE}
                players={playersToCompare}
              />
            </Box>
          </div>

          <div className={classes.actions}>
            {teamAssistant && (
              <PremiumSwitch
                renderRegularUser={() => (
                  isEmptyTeam ? (
                    <Button
                      fullWidth
                      size="large"
                      color="accent"
                      variant="contained"
                      onClick={availablePlayers && (() => teamAssistant.execute(availablePlayers, []))}
                      className={classes.teamAssistantButton}
                      disabled={isOverBudget || !isTeamAssistantEnabled}
                      disableFocusRipple
                      disableTouchRipple
                    >
                      <img src={teamAssistantIcon} alt="" className={classes.buttonIcon} />
                      <T id="teameditor_autofill" />
                    </Button>
                  ) : null
                )}
                renderPremiumUser={() => (
                  <Paper
                    color="accent"
                    className={classes.actionContainer}
                  >
                    <Button
                      fullWidth
                      size="large"
                      color="accent"
                      variant="contained"
                      onClick={availablePlayers && (() => teamAssistant.execute(availablePlayers, selectedPlayers))}
                      className={classes.teamAssistantButton}
                      disabled={!isTeamAssistantEnabled}
                    >
                      <img src={teamAssistantIcon} alt="" className={classes.buttonIcon} />
                      {isEmptyTeam && <T id="teameditor_autofill" />}
                    </Button>
                  </Paper>
                )}
              />
            )}
            {(!isEmptyTeam || !teamAssistant) && (
              <Button
                size="large"
                color="primary"
                variant="contained"
                className={classes.submitButton}
                disabled={submitting || !isValidTeam}
                onClick={() => onSubmit(selectedPlayers)}
              >
                {submitText}
              </Button>
            )}
          </div>
        </Paper>
      </Grid>

      <Grid
        item
        xs={12}
        md={narrowOnDesktop ? 6 : undefined}
      >
        <ActionContext
          isLoading={isLoading}
          errorCode={error}
          component={PlayerList}
          componentProps={{
            match: match!,
            availablePlayers: listedPlayers!,
            selectedPlayerIds: selectedPlayers.map(player => player.playerId!),
            onTogglePlayer: onPlayerClick,
            onRequestPlayerProfile: onPlayerJerseyClick,
          }}
        />
      </Grid>

      {teamAssistant && (
        <Snackbar
          open={!!teamAssistant.error}
          autoHideDuration={3000}
          onClose={teamAssistant.dismissError}
          message={<T id="teameditor_autofill_error" />}
        />
      )}
    </Grid>
  )
}

export default TeamEditor
try {
    // @ts-ignore
    TeamEditor.displayName = "TeamEditor";
    // @ts-ignore
    TeamEditor.__docgenInfo = { "description": "", "displayName": "TeamEditor", "props": { "isLoading": { "defaultValue": null, "description": "", "name": "isLoading", "required": true, "type": { "name": "boolean" } }, "error": { "defaultValue": null, "description": "", "name": "error", "required": false, "type": { "name": "any" } }, "singleSelect": { "defaultValue": null, "description": "", "name": "singleSelect", "required": false, "type": { "name": "boolean" } }, "narrowOnDesktop": { "defaultValue": null, "description": "", "name": "narrowOnDesktop", "required": false, "type": { "name": "boolean" } }, "match": { "defaultValue": null, "description": "", "name": "match", "required": false, "type": { "name": "any" } }, "selectedPlayers": { "defaultValue": null, "description": "", "name": "selectedPlayers", "required": true, "type": { "name": "any[]" } }, "listedPlayers": { "defaultValue": null, "description": "", "name": "listedPlayers", "required": false, "type": { "name": "any[]" } }, "availablePlayers": { "defaultValue": null, "description": "", "name": "availablePlayers", "required": false, "type": { "name": "any[]" } }, "playersToCompare": { "defaultValue": null, "description": "", "name": "playersToCompare", "required": true, "type": { "name": "any[]" } }, "onPlayerClick": { "defaultValue": null, "description": "", "name": "onPlayerClick", "required": true, "type": { "name": "(player: any) => void" } }, "onPlayerJerseyClick": { "defaultValue": null, "description": "", "name": "onPlayerJerseyClick", "required": true, "type": { "name": "(player: any) => void" } }, "PlayerList": { "defaultValue": null, "description": "", "name": "PlayerList", "required": true, "type": { "name": "ComponentType<any>" } }, "header": { "defaultValue": null, "description": "", "name": "header", "required": true, "type": { "name": "ReactNode" } }, "teamAssistant": { "defaultValue": null, "description": "", "name": "teamAssistant", "required": false, "type": { "name": "{ error?: any; dismissError: () => void; execute: (availablePlayers: any[], selectedPlayers: any[]) => void; }" } }, "onSubmit": { "defaultValue": null, "description": "", "name": "onSubmit", "required": true, "type": { "name": "(players: any[]) => void" } }, "submitText": { "defaultValue": null, "description": "", "name": "submitText", "required": true, "type": { "name": "ReactNode" } }, "submitting": { "defaultValue": null, "description": "", "name": "submitting", "required": true, "type": { "name": "boolean" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/TeamEditor/TeamEditor.tsx#TeamEditor"] = { docgenInfo: TeamEditor.__docgenInfo, name: "TeamEditor", path: "src/components/TeamEditor/TeamEditor.tsx#TeamEditor" };
}
catch (__react_docgen_typescript_loader_error) { }